import type { ApiErrorBody } from '@tacobots-forms/shared';
import { apiBaseUrl } from '@/app/branding';

/**
 * Thin fetch wrapper.
 *
 * Two things it guarantees: the session cookie travels with every request, and
 * every mutation carries the CSRF token that the API expects. The token is read
 * from the cookie the server set, which is exactly the half of the pair a
 * cross-site request cannot reach.
 */

const CSRF_COOKIE = 'tbf_session_csrf';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, string[]>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller simply is not signed in. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Field-level messages, flattened for a form. */
  fieldErrors(): Record<string, string> {
    const flat: Record<string, string> = {};
    for (const [path, messages] of Object.entries(this.details ?? {})) {
      if (messages[0]) flat[path] = messages[0];
    }
    return flat;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Sent as multipart/form-data instead of JSON. */
  formData?: FormData;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (method !== 'GET') {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const response = await fetch(`${apiBaseUrl}/api${path}`, {
    method,
    headers,
    body,
    credentials: 'include',
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    const error = (payload ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(
      response.status,
      error.code ?? 'ERROR',
      error.message ?? 'Something went wrong. Please try again.',
      error.details,
      error.requestId,
    );
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Absolute URL of an API endpoint, for links and <embed> sources. */
export function apiUrl(path: string): string {
  return `${apiBaseUrl}/api${path}`;
}
